import React, { useEffect, useState } from "react";
import { toJS } from "mobx";
import { observer } from "mobx-react";
import nsi from "../../../../store/nsi";
import { Row, Col } from "antd";
import { Controller, useFieldArray } from "react-hook-form";
import classNames from "classnames";
import { authStore } from "../../../../store/authStore";

import {
  FormField,
  ICheckbox,
  ISelect,
  UploadFile,
  AutoComplete,
  FormBlock,
  Skeleton,
  IDatePicker,
} from "../../../../ui-kit";

import { EVENT_TYPE_PLANNING_ID } from "../../constants";

import { IResourceFormData } from "../../interface";
import styles from "../../DefectForm.module.scss";
import AddressDisconnexion, {
  emptyAddressDisconnexion,
  TAddressDisconnexion,
} from "../addressDisconnexion";
import { PlusOutlined } from "@ant-design/icons";

const { SelectSkeleton } = Skeleton;

const ColdWaterSupplyForm: React.FC<IResourceFormData> = observer(
  ({
    formId,
    isAddMode,
    isClose,
    isReadonly,
    onChangeImage,
    setAddressInfo,
    data,
    RSOList,
    ...handleForm
  }) => {
    const { setValue, register, watchAllField, errors, control } = handleForm;
    const { defectNotify } = authStore;

    const [editRSO, setEditRSO] = useState<boolean>(false);

    useEffect(() => {
      setEditRSO(authStore.allOrgsView);
    }, []);

    const {
      fields: addressDisconnexionFields,
      append: appendAddressDisconnexion,
      remove: removeAddressDisconnexion,
    } = useFieldArray({
      control,
      name: "addressDisconnexionList",
    });

    const fiasTooltip = (
      <>
        <div>
          Начните вводить адрес с улицы и выберите значение из списка. Например:
        </div>
        <div>
          - фонтанка (для адреса г Санкт-Петербург, наб Реки Фонтанки, д 172)
        </div>
        <div>
          - линия (для адреса г Санкт-Петербург, г Сестрорецк, 9-я линия, д 6
          стр 1)
        </div>
        <div>- советская 9 (г Санкт-Петербург, ул 9-я Советская, д 3)</div>
        <div>
          - большой пр-кт (для адреса г Санкт-Петербург, пр-кт Большой В.О., д
          92 литера Г)
        </div>
        <div>
          - угловые дома - ломоносова 37/8: (для адреса г Санкт-Петербург, город
          Красное Село, ул Ломоносова, д 37/8 литера А)
        </div>
        <div>
          - 197729 (ввод индекса для поселков и н.п., г Санкт-Петербург, поселок
          Молодежное, ул Правды, д 3б)
        </div>
      </>
    );

    return (
      <div className={styles.fieldsWrap} aria-readonly aria-disabled>
        <div className={styles.fieldContainer}>
          {nsi.orgIsLoading.value ? (
            <SelectSkeleton />
          ) : (
            <Controller
              control={control}
              name="organizationId"
              render={({ field }) => (
                <ISelect
                  field={field}
                  optionsArr={RSOList}
                  placeholder="Ресурсоснабжающая организация"
                  disabled={!editRSO || isReadonly}
                  error={errors?.organizationId?.message}
                  isRequired
                />
              )}
            />
          )}
          <FormField
            label={
              watchAllField.eventTypeId === EVENT_TYPE_PLANNING_ID
                ? "ID планового события"
                : "ID дефекта"
            }
            name="externalId"
            register={register}
            type="number"
            currentValue={watchAllField.externalId}
            error={errors?.externalId?.message}
            registerOptions={{
              valueAsNumber: true,
            }}
            isRequired
            disabled={isReadonly}
          />
          <Controller
            control={control}
            name="eventTypeId"
            render={({ field }) => (
              <ISelect
                field={field}
                optionsArr={nsi.eventTypes}
                placeholder="Тип события"
                error={errors?.eventTypeId?.message}
                isRequired
                disabled={isReadonly}
              />
            )}
          />
          <FormBlock
            name="Дата и время отключения/ограничения"
            disabled={isReadonly}
          >
            <Row gutter={22}>
              <Col span={12}>
                <Controller
                  control={control}
                  name="dateTimeDisable"
                  render={({ field }) => (
                    <IDatePicker
                      field={field}
                      placeholder={"Дата"}
                      error={errors?.dateTimeDisable?.message}
                      isRequired
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <FormField
                  label="Время"
                  name="timeDisable"
                  register={register}
                  type="time"
                  currentValue={watchAllField?.timeDisable}
                  error={errors?.timeDisable?.message}
                  isRequired
                />
              </Col>
            </Row>
          </FormBlock>
          <Controller
            control={control}
            name="addressText"
            render={({ field }) => (
              <AutoComplete
                field={field}
                info={fiasTooltip}
                label={
                  watchAllField.eventTypeId === EVENT_TYPE_PLANNING_ID
                    ? "Адрес планового события"
                    : "Адрес дефекта"
                }
                error={errors?.addressGuid?.message}
                onSelect={(value: any) => setAddressInfo(value)}
                onChange={(value) => {
                  if (!value) setAddressInfo(null);
                }}
                required
                disabled={isReadonly}
              />
            )}
          />
          <Controller
            control={control}
            name="districtId"
            render={({ field }) => (
              <ISelect
                field={field}
                optionsArr={nsi.districts}
                onChange={watchAllField.districtId}
                placeholder="Административный район"
                error={errors?.districtId?.message}
                isRequired
                disabled={isReadonly}
              />
            )}
          />

          <FormField
            label="Примечание к адресу"
            name="addressNote"
            register={register}
            type="text"
            currentValue={watchAllField.addressNote}
            disabled={isReadonly}
          />

          <FormBlock
            name="Адреса отключаемых зданий или с ограничениями по ЦО"
            disabled={isReadonly}
          >
            <button
              className={styles.addButton}
              onClick={(e) =>
                appendAddressDisconnexion({ ...emptyAddressDisconnexion })
              }
              type="button"
            >
              <PlusOutlined style={{ fontSize: "24px", color: "#fff" }} />{" "}
              <span>Добавить адрес</span>
            </button>
            {addressDisconnexionFields.map((address, index) => {
              return (
                <AddressDisconnexion
                  key={address.id}
                  control={control}
                  addressDisconnexion={
                    watchAllField.addressDisconnexionList[
                      index
                    ] as TAddressDisconnexion
                  }
                  index={index}
                  setValue={setValue}
                  removeAddressDisconnexion={removeAddressDisconnexion}
                  required={!isClose}
                />
              );
            })}
          </FormBlock>
        </div>
        <div className={styles.fieldContainer}>
          <FormField
            label="Количество отключенных абонентов"
            name="disconnectedSubscribers"
            register={register}
            type="number"
            currentValue={watchAllField.disconnectedSubscribers}
            disabled={isReadonly}
          />

          <FormBlock
            name="Количество зданий в зоне отключения"
            disabled={isReadonly}
          >
            <Row gutter={22}>
              <Col span={8}>
                <FormField
                  label="Всего"
                  name="overBuildings"
                  register={register}
                  type="number"
                  disabled={true}
                  currentValue={watchAllField.overBuildings}
                  error={errors?.overBuildings?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
              <Col span={8}>
                <FormField
                  label="Жилых"
                  name="livingBuildings"
                  register={register}
                  type="number"
                  currentValue={watchAllField.livingBuildings}
                  error={errors?.livingBuildings?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
              <Col span={8}>
                <FormField
                  label="Лечебных"
                  name="hospitalBuildings"
                  register={register}
                  type="number"
                  currentValue={watchAllField.hospitalBuildings}
                  error={errors?.hospitalBuildings?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
            </Row>
            <Row gutter={22}>
              <Col span={8}>
                <FormField
                  label="Детских"
                  name="childrenBuildings"
                  register={register}
                  type="number"
                  currentValue={watchAllField.childrenBuildings}
                  error={errors?.childrenBuildings?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
              <Col span={8}>
                <FormField
                  label="Прочих"
                  name="buildings"
                  register={register}
                  type="number"
                  currentValue={watchAllField.buildings}
                  error={errors?.buildings?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
            </Row>
          </FormBlock>
          <FormBlock
            name={
              watchAllField.eventTypeId === EVENT_TYPE_PLANNING_ID
                ? "Описание планового события"
                : "Описание дефекта"
            }
            disabled={isReadonly}
          >
            <Row gutter={22}>
              <Col span={12}>
                <FormField
                  label="Диаметр трубы, мм"
                  name="diameter"
                  register={register}
                  type="number"
                  currentValue={watchAllField.diameter}
                  error={errors?.diameter?.message}
                  isRequired={!isClose ? false : true}
                />
              </Col>
              <Col span={12}>
                <Controller
                  control={control}
                  name="typeGasketId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.gasketTypes}
                      placeholder="Тип прокладки"
                      error={errors?.typeGasketId?.message}
                      isRequired={!isClose ? false : true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row gutter={22}>
              <Col span={12}>
                <div className={styles.fieldContainer__checkbox}>
                  <Controller
                    control={control}
                    name="discharge"
                    render={({ field: { value, onChange } }) => (
                      <ICheckbox
                        checked={value}
                        onChange={onChange}
                        label={"Вытекание"}
                      />
                    )}
                  />
                </div>
              </Col>
              <Col span={12}>
                <Controller
                  control={control}
                  name="defNatureId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.restrictionNatures
                        ?.filter((item) =>
                          JSON.parse(item.description).includes(formId),
                        )
                        .map((item) => ({
                          ...item,
                          description: item.title,
                          title: item.description,
                        }))}
                      placeholder={"Характер отключения"}
                      error={errors?.defNatureId?.message}
                      isRequired={!isClose ? false : true}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row gutter={22}>
              <Col span={12}>
                <Controller
                  control={control}
                  name="workStatusId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.workStatus}
                      placeholder={"Статус"}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Controller
                  control={control}
                  name="complaintCodeId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.complaintCodes}
                      placeholder={"Код жалобы"}
                    />
                  )}
                />
              </Col>
            </Row>
            <Row gutter={22}>
              <Col span={12}>
                <Controller
                  control={control}
                  name="repairCategoryId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.repairCategories}
                      placeholder="Категория"
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <Controller
                  control={control}
                  name="workProgressId"
                  render={({ field }) => (
                    <ISelect
                      field={field}
                      optionsArr={nsi.workProgresses}
                      placeholder="Ход работ"
                    />
                  )}
                />
              </Col>
            </Row>
          </FormBlock>
        </div>
        <div className={styles.fieldContainer}>
          <FormBlock
            name="Планируемая дата и время завершения работ"
            disabled={isReadonly}
          >
            <Row gutter={22}>
              <Col span={12}>
                <Controller
                  control={control}
                  name="dateTimePlanned"
                  render={({ field }) => (
                    <IDatePicker
                      field={field}
                      placeholder={"Дата"}
                      error={errors?.dateTimePlanned?.message}
                      isRequired
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <FormField
                  label="Время"
                  name="gasPlannedTime"
                  register={register}
                  type="time"
                  currentValue={watchAllField?.gasPlannedTime}
                  error={errors?.gasPlannedTime?.message}
                  isRequired
                />
              </Col>
            </Row>
            <Row gutter={22}>
              <Col span={12}>
                <FormField
                  label="Нормативное время, ч"
                  name="standartTime"
                  register={register}
                  type="decimal"
                  currentValue={watchAllField.standartTime}
                  error={errors?.standartTime?.message}
                  isRequired
                />
              </Col>
            </Row>
          </FormBlock>

          <FormBlock
            name={
              watchAllField.eventTypeId === EVENT_TYPE_PLANNING_ID
                ? "Дата и время завершения планового события"
                : "Дата и время устранения дефекта"
            }
            disabled={isReadonly}
          >
            <Row gutter={22}>
              <Col span={12}>
                <Controller
                  control={control}
                  name="dateTimeEnable"
                  render={({ field }) => (
                    <IDatePicker
                      field={field}
                      placeholder={"Дата"}
                      error={errors?.dateTimeEnable?.message}
                      isRequired={isClose}
                    />
                  )}
                />
              </Col>
              <Col span={12}>
                <FormField
                  label="Время"
                  name="timeEnable"
                  register={register}
                  type="time"
                  currentValue={watchAllField?.timeEnable}
                  error={errors?.timeEnable?.message}
                  isRequired={isClose}
                />
              </Col>
            </Row>
          </FormBlock>

          <FormField
            label="ФИО ответственного"
            name="responsible"
            register={register}
            type="text"
            currentValue={watchAllField.responsible}
            error={errors?.responsible?.message}
            isRequired
            disabled={isReadonly}
          />

          <FormField
            label="Телефон ответственного"
            name="responsiblePhone"
            register={register}
            type="tel"
            currentValue={watchAllField.responsiblePhone}
            error={errors?.responsiblePhone?.message}
            isRequired
            disabled={isReadonly}
          />

          <FormField
            label="Комментарий"
            name="comment"
            register={register}
            type="text"
            currentValue={watchAllField.comment}
            disabled={isReadonly}
          />

          <UploadFile
            onChange={onChangeImage}
            accept="image"
            label="Загрузить файл"
            files={toJS(data?.files)}
            isDisabled={isReadonly}
          />

          {authStore && defectNotify && (
            <div className={styles.form__container}>
              <Controller
                control={control}
                name="mediaCoverage"
                render={({ field: { value, onChange } }) => (
                  <>
                    <ICheckbox
                      checked={value}
                      onChange={onChange}
                      label={"Признак СМИ"}
                      className={classNames(styles.form__checkBoxTitle, {
                        [styles.form__checked]: value,
                      })}
                      disabled={isReadonly}
                    />
                    {value && (
                      <div className={styles.form__item}>
                        <Row gutter={[0, 22]}>
                          <FormField
                            label="Комментарий СМИ"
                            name="mediaComment"
                            register={register}
                            type="textarea"
                            currentValue={watchAllField.mediaComment}
                            error={errors?.mediaComment?.message}
                            disabled={isReadonly}
                          />
                        </Row>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

export default ColdWaterSupplyForm;
